class Mengenrechner {
  constructor() {
    this.selectors = {
      optionInfo: '.variant-picker__option-info',
      optionValue: '.variant-picker__option-info variant-option-value',
      popup: '.mengenrechner-popup',
      input: '.mengenrechner-input',
      result: '.mengenrechner-result',
      close: '.mengenrechner-popup-close',
      qtyBtn: '.mengenrechner-qty-btn',
      link: '.mengenrechner-link',
    };

    // Nur die Fläche; der Satz drumherum kommt in der Sprache der Seite aus dem Snippet (data-t-deck, 25.09.2026).
    this.coverageMap = new Map([
      ['1 L', '10–12'],
      ['2.5 L', '25–30'],
      ['2,5 L', '25–30'],
      ['5 L', '50–60'],
      ['10 L', '100–120'],
      ['20 L', '200–240'],
      ['30 kg', '200–240'],
    ]);

    this.coverageClass = 'coverage-info-text';
    this.maxValue = 9999999;

    this.init();
  }

  /* ---------- UTILS ---------- */

  normalize(text) {
    return text.trim().replace(/\s+/g, ' ');
  }

  findCoverage(text) {
    // Klein geschrieben verglichen: auf den Fremdsprachen-Seiten heissen die Werte „1 l“, „2,5 l“ (25.09.2026).
    const normalized = this.normalize(text).replace(',', '.').toLowerCase();
    for (const [key, value] of this.coverageMap) {
      if (normalized.includes(key.replace(',', '.').toLowerCase())) {
        return value;
      }
    }
    return null;
  }

  cleanValue(value) {
    return value.replace(/\D/g, '').slice(0, 7);
  }

  validate(value, strict = false) {
    let cleaned = this.cleanValue(value);

    if (!cleaned) return strict ? '1' : '';

    let num = parseInt(cleaned, 10);
    if (num < 1) return '1';
    if (num > this.maxValue) return String(this.maxValue);

    return cleaned;
  }

  /* ---------- COVERAGE ---------- */

  updateCoverage() {
    const info = document.querySelector(this.selectors.optionInfo);
    const value = info?.querySelector(this.selectors.optionValue);
    if (!info || !value) return;

    const coverage = this.findCoverage(value.textContent);
    const existing = info.querySelector(`.${this.coverageClass}`);

    if (!coverage) {
      existing?.remove();
      return;
    }

    const text = ` (${this.t.deck.replace('{a}', coverage)})`;
    if (existing) {
      existing.textContent = text;
    } else {
      const span = document.createElement('span');
      span.className = this.coverageClass;
      span.textContent = text;
      info.appendChild(span);
    }
  }

  /* ---------- POPUP ---------- */

  openPopup() {
    this.popup.classList.add('active');
    this.popup.setAttribute('aria-hidden', 'false');
    setTimeout(() => this.input.focus(), 100);
  }

  closePopup() {
    this.popup.classList.remove('active');
    this.popup.setAttribute('aria-hidden', 'true');
    this.input.value = '1';
    this.calculate();
  }

  calculate() {
    const area = parseInt(this.input.value, 10) || 1;
    // Halbe Liter (Finn 24.09.2026): 25 m² -> 2,5 Liter, passend zu "2,5 L reicht fuer 25–30 m²"; mindestens 1 Liter.
    const liters = Math.max(1, Math.ceil(area * 2 / 10) / 2);
    this.result.textContent = `${String(liters).replace('.', ',')} ${liters <= this.t.l1bis ? this.t.l1 : this.t.ln}`;
  }

  handleQty(action) {
    let value = parseInt(this.input.value, 10) || 1;

    if (action === 'increase') value++;
    if (action === 'decrease') value--;

    value = Math.max(1, Math.min(this.maxValue, value));
    this.input.value = value.toString();
    this.calculate();
  }

  /* ---------- EVENTS ---------- */

  bindEvents() {
    document.addEventListener('click', (e) => {
      if (e.target.closest(this.selectors.link)) {
        e.preventDefault();
        this.openPopup();
      }

      if (e.target.closest(this.selectors.close)) {
        this.closePopup();
      }

      const qtyBtn = e.target.closest(this.selectors.qtyBtn);
      if (qtyBtn) {
        this.handleQty(qtyBtn.dataset.action);
      }
    });

    // preselect default 1
    this.input.addEventListener('focus', () => {
      if (this.input.value === '1') {
        this.input.select();
      }
    });

    this.input.addEventListener('input', (e) => {
      e.target.value = this.validate(e.target.value);
      this.calculate();
    });

    this.input.addEventListener('blur', (e) => {
      e.target.value = this.validate(e.target.value, true);
      this.calculate();
    });

    this.input.addEventListener('keydown', (e) => {
      const allowed = [
        'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
        'Tab', 'Enter', 'Home', 'End'
      ];

      if (allowed.includes(e.key)) return;

      // block all except digits
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
      }

      // limit 7 digits
      if (
        this.input.value.length >= 7 &&
        this.input.selectionStart === this.input.selectionEnd
      ) {
        e.preventDefault();
      }
    });

    this.input.addEventListener('paste', (e) => {
      e.preventDefault();
      const text = (e.clipboardData || window.clipboardData).getData('text');
      this.input.value = this.validate(text);
      this.calculate();
    });

    document.addEventListener('change', (e) => {
      if (e.target.matches('input[type="radio"]')) {
        this.updateCoverage();
      }
    });
  }

  /* ---------- INIT ---------- */

  init() {
    this.popup = document.querySelector(this.selectors.popup);
    this.input = document.querySelector(this.selectors.input);
    this.result = document.querySelector(this.selectors.result);

    if (!this.popup || !this.input || !this.result) return;

    // Texte in der Sprache der Seite (snippets/quantity_calculator.liquid, 25.09.2026); ohne sie Deutsch wie bisher.
    const d = this.popup.dataset;
    this.t = {
      deck: d.tDeck || 'reicht für {a} m²',
      l1: d.tL1 || 'Liter',
      ln: d.tLn || 'Liter',
      l1bis: parseFloat(d.tL1Bis) || 1,
    };

    this.updateCoverage();
    this.bindEvents();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new Mengenrechner();
});
